<template>
  <div class="cns">
    <!-- Zona 1: Canales -->
   <section class="cns-group">
      <span class="cns-group-title">Canales disponibles</span>
      <label class="cns-toggle-row"><input type="checkbox" v-model="platformEnabled" @change="onGlobalChange" /> Notificaciones en plataforma</label>
      <label class="cns-toggle-row"><input type="checkbox" v-model="emailEnabled" @change="onGlobalChange" /> Notificaciones por correo</label>

    </section>

    <!-- Zona 2: Tabla de eventos -->
    <section class="cns-group">
      <span class="cns-group-title">Configuración por evento</span>
      <span class="cns-group-sub">Define para cada evento si genera notificación, por qué canal, y a quién llega.</span>

      <div v-if="!platformEnabled" class="cns-banner">Las notificaciones en plataforma están desactivadas globalmente. Actívalas arriba para que esta configuración tenga efecto.</div>
      <div v-if="!emailEnabled" class="cns-banner">Las notificaciones por correo están desactivadas globalmente. Actívalas arriba para que esta configuración tenga efecto.</div>

      <div class="cns-table">
        <div class="cns-table-header">
          <span class="cns-col-event">Evento</span>
          <span class="cns-col-toggle">Plataforma</span>
          <span class="cns-col-toggle">Correo</span>
          <span class="cns-col-recipient">Destinatario</span>
        </div>

        <template v-for="mod in modules" :key="mod">
          <div class="cns-module-row"><span class="mdi" :class="eventsByModule[mod][0].icon"></span> {{ mod }}</div>

         <div v-for="ev in eventsByModule[mod]" :key="ev.key" class="cns-event-row">
          <span class="cns-col-event">{{ ev.label }}</span>
          <span class="cns-col-toggle">
            <input type="checkbox" v-model="ev.platformEnabled" :disabled="!platformEnabled" :class="{ 'cns-toggle--dimmed': !platformEnabled }" @change="onEventChange(ev)" />
          </span>
          <span class="cns-col-toggle">
            <input type="checkbox" v-model="ev.emailEnabled" :disabled="!emailEnabled" :class="{ 'cns-toggle--dimmed': !emailEnabled }" @change="onEventChange(ev)" />
          </span>
          <span class="cns-col-recipient">
            <select v-model="ev.recipientMode" class="cns-select" @change="onEventChange(ev)">
              <option value="ACTIVE_ADMIN">Administrador activo</option>
              <option value="ALL_ADMINS">Todos los administradores</option>
              <option value="SPECIFIC_EMAIL">Correo específico</option>
            </select>
            <input v-if="ev.recipientMode === 'SPECIFIC_EMAIL'" v-model="ev.specificEmail" class="cns-input-inline" placeholder="correo@ejemplo.com" @change="onEventChange(ev)" />
          </span>
        </div>
        </template>

        <div class="cns-event-row cns-event-row--locked">
          <span class="cns-col-event">{{ lockedEvent.label }} <span class="mdi mdi-lock-outline cns-lock-icon"></span></span>
          <span class="cns-col-toggle"><input type="checkbox" checked disabled /></span>
          <span class="cns-col-toggle"><input type="checkbox" checked disabled /></span>
          <span class="cns-col-recipient cns-locked-note">Siempre activo para garantizar la operación del sistema.</span>
        </div>
      </div>
    </section>

    <button class="cns-save-btn" @click="onSaveAll">Guardar configuración de notificaciones</button>
    <span class="cns-save-note">Los cambios aplican a notificaciones generadas a partir de ahora. Las notificaciones ya enviadas no se modifican.</span>

    <Transition name="cns-toast-fade">
      <div v-if="toastMessage" class="cns-toast"><span class="mdi mdi-check-circle"></span> {{ toastMessage }}</div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getNotificationConfig, saveGlobalChannels, saveEventConfig } from '@/services/parkConfig'

const props = defineProps({ parkId: { type: String, required: true } })
const emit = defineEmits(['dirty-change'])

const platformEnabled = ref(true)
const emailEnabled = ref(true)
const events = ref([])
const lockedEvent = ref({ key: '', module: '', icon: '', label: '' })
const toastMessage = ref(null)
const pendingEventSaves = ref(new Set())

async function load() {
  const config = await getNotificationConfig(props.parkId)
  platformEnabled.value = config.notificationsPlatformEnabled
  emailEnabled.value = config.notificationsEmailEnabled
  events.value = config.events
  lockedEvent.value = config.lockedEvent
}

onMounted(load)

const modules = computed(() => [...new Set(events.value.map((e) => e.module))])
const eventsByModule = computed(() => {
  const map = {}
  for (const ev of events.value) {
    if (!map[ev.module]) map[ev.module] = []
    map[ev.module].push(ev)
  }
  return map
})

function showToast(msg) {
  toastMessage.value = msg
  setTimeout(() => (toastMessage.value = null), 3000)
}

async function onGlobalChange() {
  await saveGlobalChannels(props.parkId, { notificationsPlatformEnabled: platformEnabled.value, notificationsEmailEnabled: emailEnabled.value })
}

function onEventChange(ev) {
  pendingEventSaves.value.add(ev.key)
  emit('dirty-change', true)
}

async function onSaveAll() {
  for (const key of pendingEventSaves.value) {
    const ev = events.value.find((e) => e.key === key)
    if (ev) await saveEventConfig(props.parkId, key, ev)
  }
  pendingEventSaves.value = new Set()
  emit('dirty-change', false)
  showToast('Configuración de notificaciones actualizada')
}
</script>

<style scoped>
.cns { display: flex; flex-direction: column; gap: 1.5rem; max-width: 900px; }
.cns-group { display: flex; flex-direction: column; gap: 0.5rem; padding-bottom: 1.5rem; border-bottom: 1px solid #f1f5f9; }
.cns-group-title { font-family: var(--font-body); font-size: 12px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.8px; color: #64748b; }
.cns-group-sub { font-family: var(--font-body); font-size: 11px; color: #94a3b8; margin-top: -0.3rem; }

.cns-toggle-row { display: flex; align-items: center; gap: 0.4rem; font-family: var(--font-body); font-size: 13px; color: #1e3a5f; }

.cns-banner { height: 32px; display: flex; align-items: center; padding: 0 0.75rem; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; font-family: var(--font-body); font-size: 11px; color: #92400e; }

.cns-table { border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
.cns-table-header { display: flex; align-items: center; height: 36px; background: #f8f9fc; border-bottom: 1px solid #e2e8f0; padding: 0 0.75rem; gap: 0.5rem; }
.cns-table-header span { font-family: var(--font-body); font-size: 10px; font-weight: 500; text-transform: uppercase; color: #94a3b8; }

.cns-col-event { flex: 0 0 45%; }
.cns-col-toggle { flex: 0 0 15%; display: flex; justify-content: center; }
.cns-col-recipient { flex: 0 0 25%; display: flex; align-items: center; gap: 0.3rem; }

.cns-module-row { display: flex; align-items: center; gap: 0.4rem; height: 28px; background: #f8f9fc; padding: 0 0.75rem; font-family: var(--font-body); font-size: 11px; font-weight: 500; text-transform: uppercase; color: #64748b; }
.cns-module-row .mdi { font-size: 12px; }

.cns-event-row { display: flex; align-items: center; height: 44px; padding: 0 0.75rem; gap: 0.5rem; border-bottom: 1px solid #f1f5f9; font-family: var(--font-body); font-size: 12px; color: #1e3a5f; }
.cns-event-row--locked { background: #fafafa; }

.cns-toggle--dimmed { opacity: 0.4; }
.cns-select { font-family: var(--font-body); font-size: 11px; border: 1px solid #e2e8f0; border-radius: 4px; padding: 0.25rem 0.35rem; }
.cns-input-inline { font-family: var(--font-body); font-size: 11px; border: 1px solid #e2e8f0; border-radius: 4px; padding: 0.25rem 0.35rem; width: 130px; }

.cns-lock-icon { font-size: 10px; color: #cbd5e1; }
.cns-locked-note { font-family: var(--font-body); font-size: 10px; color: #94a3b8; font-style: italic; }

.cns-save-btn { align-self: flex-start; background: #3b82f6; color: #ffffff; border: none; border-radius: 6px; padding: 0.6rem 1.2rem; font-family: var(--font-body); font-size: 13px; font-weight: 500; cursor: pointer; }
.cns-save-note { font-family: var(--font-body); font-size: 10px; color: #94a3b8; }

.cns-toast { position: fixed; top: 1rem; right: 1rem; display: flex; align-items: center; gap: 0.4rem; background: #1e3a5f; color: #ffffff; padding: 0.6rem 1rem; border-radius: 8px; font-family: var(--font-body); font-size: 13px; box-shadow: 0 8px 24px rgba(0,0,0,0.2); z-index: 3000; }
.cns-toast .mdi { color: #10b981; }
.cns-toast-fade-enter-active, .cns-toast-fade-leave-active { transition: opacity 300ms ease; }
.cns-toast-fade-enter-from, .cns-toast-fade-leave-to { opacity: 0; }


.cns-input-full { width: 100%; box-sizing: border-box; font-family: var(--font-body); font-size: 13px; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.5rem 0.6rem; }
.cns-link { color: #3b82f6; }




.cns-col-event {
  flex: 0 0 34%;
  min-width: 0;
}

.cns-col-toggle {
  flex: 0 0 13%;
  display: flex;
  justify-content: center;
  min-width: 0;
}

.cns-col-recipient {
  flex: 0 0 21%;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  min-width: 0;
}
</style>